import { IdGenerator, IdGeneratorEntityType } from './IdGenerator';
import { parseTextIntoTokens } from './textParsing';

import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
  TranscriptPunctToken,
} from '../types';

/**
 * Splits a text value into multiple tokens with evenly distributed timestamps.
 * This is the core utility used by both transcript editing and segment creation.
 *
 * @param value - The text value to split (e.g., "hello world")
 * @param mediaStart - Media start time for the interval
 * @param relativeStart - Relative start time for the interval
 * @param relativeDuration - Available duration to distribute across words
 * @param initialIndex - Initial index for the tokens (defaults to -1)
 * @param state - State for the tokens (e.g., 'added', undefined)
 * @returns Array of ClipTranscriptToken with timestamps evenly distributed
 */
export function splitTokenValueIntoTokens(
  value: string,
  mediaStart: number,
  relativeStart: number,
  relativeDuration: number,
  initialIndex: number = -1,
  state: ClipTranscriptToken['state'],
): ClipTranscriptToken[] {
  const parsedTokens = parseTextIntoTokens(value);
  const textTokensCount = parsedTokens.filter(t => t.type === 'text').length;

  const generator = IdGenerator.get(
    IdGeneratorEntityType.TranscriptToken,
    'hex',
  );

  let perWordDuration = 0;
  if (textTokensCount > 0) {
    perWordDuration = Math.max(0, relativeDuration / textTokensCount);
  }

  let wordIndex = 0;
  return parsedTokens.map<ClipTranscriptToken>(raw => {
    if (raw.type === 'text') {
      const relativeStartTime = relativeStart + wordIndex * perWordDuration;
      const relativeEndTime =
        textTokensCount > 0
          ? relativeStart + (wordIndex + 1) * perWordDuration
          : relativeStart; // zero-length interval
      const mediaStartTime = mediaStart + (relativeStartTime - relativeStart);
      const mediaEndTime = mediaStart + (relativeEndTime - relativeStart);

      const token: ClipTranscriptTextToken = {
        id: generator.getNextIdHex(),
        type: 'text',
        value: raw.value,
        relative_ts: relativeStartTime,
        relative_end_ts: relativeEndTime,
        media_ts: mediaStartTime,
        media_end_ts: mediaEndTime,
        initial_index: initialIndex,
        state,
      };
      wordIndex += 1;
      return token;
    }
    const punctToken: TranscriptPunctToken = {
      id: generator.getNextIdHex(),
      type: 'punct',
      value: raw.value,
      initial_index: initialIndex,
      state,
    };
    return punctToken;
  });
}

/**
 * Checks if a text value contains multiple words (fast check using whitespace)
 * @param value - The text value to check
 * @returns True if the value contains whitespace (multiple words)
 */
export function isMultiWord(value: string): boolean {
  return /\s/.test(value);
}

/**
 * Splits ClipTranscriptToken elements that contain multiple words into separate word tokens.
 * Text tokens with multiple words will be split with evenly distributed timestamps.
 *
 * @param tokens - Array of ClipTranscriptToken to process
 * @returns New array with multi-word text tokens split into individual words
 */
export function splitMultiWordClipTokens(
  tokens: ClipTranscriptToken[],
): ClipTranscriptToken[] {
  const result: ClipTranscriptToken[] = [];

  for (const token of tokens) {
    // Pass through punctuation unchanged
    if (token.type !== 'text') {
      result.push(token);
      continue;
    }

    const textToken = token as ClipTranscriptTextToken;

    if (!textToken.value) {
      continue;
    }

    // Fast check: if no whitespace, it's a single word
    if (!isMultiWord(textToken.value)) {
      result.push(token);
      continue;
    }

    // Split the multi-word token into individual words
    const relativeDuration = textToken.relative_end_ts - textToken.relative_ts;
    const splitTokens = splitTokenValueIntoTokens(
      textToken.value,
      textToken.media_ts,
      textToken.relative_ts,
      relativeDuration,
      textToken.initial_index,
      textToken.state,
    );

    result.push(...splitTokens);
  }

  return result;
}
